// @flow

import { alertShow } from "./alert";
import fetchJSON, { checkStatus, fetchLastModified, fetchUntilChanged, postJSON } from "../fetch-json";
import { communicationsError, communicationsInProgress, communicationsSuccess } from "./communications";
import { connect, disconnect, join } from "../channel";
import { databaseUpdate } from "./database";
import history from "../history";
import { serverTimeDiffUpdate } from "./time";
import { toISOLocalString } from "../date";
import { getUpcomingTournaments, tournamentSubscribe } from "./tournament";
import { updateAvatarTimestamp } from "./user";
import { loadData, sendMessage, wrapWithAlertError, wrapWithAlertSuccess, wrapWithComms } from "./async";
import { trackUserId } from "../analytics";

import type { Dispatch } from "redux";

export const CURRENT_IDENTITY_QUESTION = "CURRENT_IDENTITY_QUESTION";
export const CURRENT_IDENTITY_ANSWER_SELECTED = "CURRENT_IDENTITY_ANSWER_SELECTED";
export const PAGE_TRANSITIONING = "PAGE_TRANSITIONING";
export const SESSION_HIDE_LOGIN = "SESSION_HIDE_LOGIN";
export const SESSION_SHOW_LOGIN = "SESSION_SHOW_LOGIN";
export const SESSION_LOGOUT = "SESSION_LOGOUT";

export function pageTransitioning() {
  return { type: PAGE_TRANSITIONING };
}

export function signUp(firstName: string, lastName: string, emailAddress: string, userName: string, password: string, state: string) {
  return (dispatch: Dispatch) => {
    dispatch(communicationsInProgress("signUp"));

    fetchJSON("/api/v1/registrations", {
      method: "POST",
      credentials: "same-origin",
      body: JSON.stringify({
        "first_name": firstName,
        "last_name": lastName,
        email: emailAddress,
        username: userName,
        password: password,
        state: state,
        "white_label_site": window.env.whiteLabelSite
      })
    })
      .then(userLoginInfo => {
        handleLoginInfo(dispatch, userLoginInfo, true);
        dispatch(communicationsSuccess("signUp"));
        history.push("/welcome");
      })
      .catch(errors => {
        dispatch(communicationsError("signUp", errors));
      });
  };
}

export function sessionHideLogin() {
  return { type: SESSION_HIDE_LOGIN };
}

export function sessionShowLogin() {
  return { type: SESSION_SHOW_LOGIN };
}

export function sessionConnect(currentUserId: ?number, token: ?string) {
  var firstConnect = true;
  return (dispatch: Dispatch) => {
    if (token === undefined || currentUserId === undefined) {
      currentUserId = window.localStorage.getItem("currentUserId");
      token = window.localStorage.getItem("webSessionToken");
    }
    if (currentUserId !== undefined) {
      trackUserId(currentUserId);
    }
    if (!firstConnect) {
      dispatch(communicationsInProgress("sessionConnect"));
    }

    function onOpen() {
      dispatch(communicationsSuccess("sessionConnect"));
      firstConnect = false;
      sessionCreate(currentUserId)(dispatch);
      setupTimeOffset(dispatch);
      getUpcomingTournaments(dispatch);
      setupOpenGroupBettingPools(dispatch);
    }
    function onClose() {
      dispatch(communicationsInProgress("sessionConnect"));
    }
    function onError() {
      if (firstConnect) {
        firstConnect = false;
        window.localStorage.removeItem("currentUserId");
        window.localStorage.removeItem("webSessionToken");
        token = null;
        currentUserId = undefined;
        disconnect(() => connect({ "white_label_site": window.env.whiteLabelSite, token }, onOpen, onClose, onError));
      }
    }

    connect({ "white_label_site": window.env.whiteLabelSite, token }, onOpen, onClose, onError);
  };
}

function sessionCreate(currentUserId) {
  return dispatch => {
    if (currentUserId) {
      dispatch(communicationsInProgress("sessionCreate"));
      var sessionChannel = join("session:" + currentUserId, (reply) => {
        dispatch(communicationsSuccess("sessionCreate"));

        var tournamentIds = reply.tournament_ids;
        delete reply.tournament_ids;

        dispatch(databaseUpdate(reply));

        tournamentIds.forEach((tournamentId) => {
          tournamentSubscribe(tournamentId)(dispatch);
        });
      }, (err) => {
        dispatch(communicationsError("sessionCreate", err));
      });
      sessionChannel.on("update", (tables) => {
        dispatch(databaseUpdate(tables));
      });
      sessionChannel.on("session_expired", () => {
        console.log("session_expired");
        sessionLogout()(dispatch);
        if (window.env.customLoginLinks) {
          window.location = window.env.customLoginLinks.login;
        }
      });
    }
  };
}

function setupTimeOffset(dispatch) {
  function dispatchTimeOffset(serverTime: ?string) {
    var diff = 0;
    if (serverTime) {
      var st = new Date(serverTime);
      diff = new Date() - st;
    }
    dispatch(serverTimeDiffUpdate(diff));
  }
  var nowChannel = join("time:now", ({ now }) => dispatchTimeOffset(now));
  nowChannel.on("now", ({ now }) => dispatchTimeOffset(now));
}

function setupOpenGroupBettingPools(dispatch) {
  dispatch(communicationsInProgress("fetchOpenGroupBettingPools"));
  function processGroupBettingPools(tables) {
    dispatch(databaseUpdate(tables));
    dispatch(communicationsSuccess("fetchOpenGroupBettingPools"));
  }
  join("group_betting_pool:open", processGroupBettingPools)
    .on("update", (tables) => {
      dispatch(databaseUpdate(tables));
    });
}

// Note: this should only be called after we're connected
export function sessionLogin(email: string, password: string, rememberMe: boolean) {
  return (dispatch: Dispatch) => {
    var promise = fetchJSON("/api/v1/sessions", {
      method: "POST",
      credentials: "same-origin",
      body: JSON.stringify({
        session: {
          email: email,
          password: password,
          whiteLabelSite: window.env.whiteLabelSite
        }
      })
    })
      .then((userLoginInfo) => handleLoginInfo(dispatch, userLoginInfo, rememberMe));

    wrapWithComms(promise, dispatch, "sessionLogin");
  };
}

// Note: this should only be called before we're connected
export function sessionTokenLogin(authenticationToken: string, rememberMe: boolean) {
  return (dispatch: Dispatch) => {
    var promise = postJSON("/api/v1/sessions", {
      session: {authentication_token: authenticationToken, white_label_site: window.env.whiteLabelSite} // eslint-disable-line camelcase
    })
      .then((userLoginInfo) => handleLoginInfo(dispatch, userLoginInfo, rememberMe))
      .catch((errors) => {
        if (window.env.customLoginLinks) {
          window.location = window.env.customLoginLinks.login;
        } else {
          throw errors;
        }
      });

    wrapWithComms(promise, dispatch, "sessionLogin");
  };
}

function handleLoginInfo(dispatch, userLoginInfo, rememberMe) {
  if (rememberMe) {
    window.localStorage.setItem("currentUserId", userLoginInfo.user_id);
    window.localStorage.setItem("webSessionToken", userLoginInfo.token);
  }
  dispatch(communicationsInProgress("sessionConnect"));
  disconnect(() => {
    dispatch(sessionHideLogin());
    sessionConnect(userLoginInfo.user_id, userLoginInfo.token)(dispatch);
  });
}

export function sessionLogout() {
  return (dispatch: Dispatch) => {
    window.localStorage.removeItem("currentUserId");
    window.localStorage.removeItem("webSessionToken");
    disconnect(() => {
      sessionConnect()(dispatch);
      dispatch({ type: SESSION_LOGOUT });
    });
  };
}

export function submitProfile(userId: number, firstName: string, lastName: string, email: string) {
  return (dispatch: Dispatch) => {
    submitForm(dispatch, userId, "update_account_details", {
      "first_name": firstName,
      "last_name": lastName,
      email: email
    }, "Your account details were updated successfully!");
  };
}

export function updateBillingInformation(userId: number, address: string, city: string, state: string, zip: string, dob: Date) {
  return (dispatch: Dispatch) => {
    var message = buildBillingInformation(address, city, state, zip, dob);
    submitForm(dispatch, userId, "update_billing_information", message, "Your billing information was updated successfully!");
  };
}

export function updateBillingInformationDeposit(userId: number, address: string, city: string, state: string, zip: string, dob: Date) {
  return (dispatch: Dispatch) => {
    var message = buildBillingInformation(address, city, state, zip, dob);
    submitForm(dispatch, userId, "update_billing_information", message)
      .then(() => history.push("/deposit/method"));
  };
}

function buildBillingInformation(address, city, state, zip, dob) {
  var dateString;
  if (dob) {
    dateString = toISOLocalString(dob);
  }
  return {
    address,
    city,
    state,
    "zip_code": zip,
    "date_of_birth": dateString
  };
}

export function updateNtraTourId(userId: number, ntraTourId: number) {
  return (dispatch: Dispatch) => {
    submitForm(dispatch, userId, "update_ntra_id", {
      "ntra_id": ntraTourId
    }, "Your NTRA Tour ID was updated successfully!");
  };
}

export function updatePassword(userId: number, password: string, newPassword: string, verifyPassword: string) {
  return (dispatch: Dispatch) => {
    if (newPassword !== verifyPassword) {
      dispatch(alertShow("notification--error", "dw-blocked", "Error", "An error is preventing the form from being submitted."));
      dispatch(communicationsError("update_password", { "verify_password": ["must match new password."] }));
    } else {
      submitForm(dispatch, userId, "update_password", {
        "password": password,
        "new_password": newPassword
      }, "Your password was updated successfully!");
    }
  };
}

type Preferences = {
  hide_realname: boolean
};

export function updatePreferences(userId: number, preferences: Preferences) {
  return (dispatch: Dispatch) => {
    submitForm(dispatch, userId, "update_preferences", preferences, "Your preferences were updated successfully!");
  };
}

export function loadTransactions(userId: number, page: number, pageSize: number) {
  return (dispatch: Dispatch) => {
    var data = { page, "page_size": pageSize };
    loadData(dispatch, "session:" + userId, "transactions", data);
  };
}

function submitForm(dispatch, userId, path, data, successMessage, errorMessage = "An error is preventing the form from being submitted.") {
  var promise = loadData(dispatch, "session:" + userId, path, data);
  if (successMessage) {
    promise = wrapWithAlertSuccess(promise, dispatch, successMessage);
  }
  if (errorMessage) {
    promise = wrapWithAlertError(promise, dispatch, () => errorMessage);
  }
  return promise;
}

export function uploadAvatar(userId: number, image: File) {
  var event = "avatar_upload_url";
  var channel = "session:" + userId;

  var avatarUrl = "https://s3.amazonaws.com/" + window.env.avatarBucket + "/resized/300/" + userId + ".jpg?t=" + new Date().getTime();

  return (dispatch: Dispatch) => {
    var uploadUrlPromise = sendMessage(join(channel), event, {});
    var lastModifiedPromise = fetchLastModified(avatarUrl);

    var promise = Promise.all([uploadUrlPromise, lastModifiedPromise])
      .then(([{ avatar_upload_url: avatarUploadUrl }, lastModified]) => {
        return fetch(avatarUploadUrl, {
          method: "PUT",
          body: image
        })
          .then(checkStatus)
          .then(() => fetchUntilChanged(avatarUrl, lastModified));
      })
      .then(() => {
        dispatch(updateAvatarTimestamp(userId));
      });
    promise = wrapWithComms(promise, dispatch, event);
    promise = wrapWithAlertSuccess(promise, dispatch, "Your profile picture was uploaded successfully!");
    wrapWithAlertError(promise, dispatch, () => "There was an error uploading your profile picture.");
  };
}

export function setCurrentIdentityQuestion() {
  return { type: CURRENT_IDENTITY_QUESTION };
}

export function setCurrentIdentityAnswerSelected(index: number) {
  return { type: CURRENT_IDENTITY_ANSWER_SELECTED, index };
}
